import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';

export const RunButton = ({ testId, onClick, loading, children, className = '' }) => (
  <Button
    data-testid={testId}
    onClick={onClick}
    disabled={loading}
    className={`bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white ${className}`}
  >
    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
    {children}
  </Button>
);

export const InfoCard = ({ title, subtitle, children }) => (
  <Card>
    <CardContent className="pt-5">
      {title && (
        <div className="mb-3">
          <h3 className="font-display text-xl text-icrisat-green">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </CardContent>
  </Card>
);

export const Kv = ({ label, value, unit }) => (
  <div className="flex flex-col rounded-lg bg-icrisat-green-soft/70 border border-icrisat-green/20 p-3">
    <span className="text-xs uppercase tracking-wider text-icrisat-earth">{label}</span>
    <span className="font-display text-2xl text-icrisat-green mt-1">
      {value ?? '—'} {unit && <span className="text-sm font-normal text-icrisat-earth">{unit}</span>}
    </span>
  </div>
);

export const AiAdvisor = ({ context, payload, disabled }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('');

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.aiAdvisor({ context, payload });
      setText(r.text);
      setSource(r.source);
      if (r.source === 'fallback' && r.error) toast.error(`AI advisor unavailable (${r.source})`);
    } catch (e) { toast.error('AI advisor failed'); } finally { setLoading(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-icrisat-green/40 p-4 bg-white/60">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-icrisat-terracotta" />
        <h4 className="font-medium text-icrisat-green">AI Advisor summary</h4>
        <span className="text-xs text-muted-foreground">Claude Sonnet 4.5 · via Emergent LLM key</span>
      </div>
      {!text && (
        <Button
          data-testid={APP.actions.runAiAdvisor}
          size="sm"
          variant="outline"
          onClick={run}
          disabled={disabled || loading}
          className="border-icrisat-terracotta text-icrisat-terracotta hover:bg-icrisat-terracotta hover:text-white"
        >
          {loading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Writing…</> : 'Get plain-language summary'}
        </Button>
      )}
      {text && (
        <div className="text-sm leading-relaxed text-icrisat-ink/90 whitespace-pre-wrap">
          {text}
          <div className="mt-2 text-xs text-muted-foreground">source: {source}</div>
        </div>
      )}
    </div>
  );
};

export const NoResult = ({ children = 'Run the advisory to see results.' }) => (
  <div className="text-sm text-muted-foreground italic p-6 border border-dashed border-border rounded-lg text-center">
    {children}
  </div>
);
